/**
 * License gate. When NgageCode is built/configured to talk to a NgageCode Cloud gateway,
 * this blocks the app behind a login until a valid license is present. When NO gateway is
 * configured (dev / team.env mode) OR not running in the desktop shell, it renders children
 * transparently — so web + legacy modes are unaffected.
 */
import { createResource, createSignal, Match, Show, Switch, type JSX } from "solid-js"
import { Button } from "@opencode-ai/ui/button"
import { Logo } from "@opencode-ai/ui/logo"

type LicenseState = {
  authenticated: boolean
  tier?: string
  email?: string
  needsUpdate?: boolean
  offline?: boolean
  reason?: string
}

type DesktopApi = {
  licenseGatewayConfigured?: () => Promise<boolean>
  licenseStatus?: () => Promise<LicenseState>
  licenseLogin?: (input: { email?: string; password?: string; devEmail?: string }) => Promise<LicenseState>
  licenseSignup?: (input: { email: string; password: string; orgName?: string }) => Promise<{ ok: boolean; reason?: string }>
  relaunch?: () => void
}

function api(): DesktopApi | undefined {
  return (window as unknown as { api?: DesktopApi }).api
}

export function LicenseGate(props: { children: JSX.Element }): JSX.Element {
  const [gate, { mutate }] = createResource(async () => {
    const a = api()
    // No desktop bridge (web) or no gateway configured → no gate.
    if (!a?.licenseGatewayConfigured) return { gated: false as const }
    const configured = await a.licenseGatewayConfigured()
    if (!configured) return { gated: false as const }
    const status = (await a.licenseStatus?.()) ?? { authenticated: false }
    return { gated: true as const, status }
  })

  const [email, setEmail] = createSignal("")
  const [password, setPassword] = createSignal("")
  const [mode, setMode] = createSignal<"login" | "signup">("login")
  const [busy, setBusy] = createSignal(false)
  const [error, setError] = createSignal<string | undefined>()
  const [notice, setNotice] = createSignal<string | undefined>()

  async function doSubmit(e: Event) {
    e.preventDefault()
    const a = api()
    if (!a?.licenseLogin || !email().trim() || !password()) return
    setBusy(true)
    setError(undefined)
    setNotice(undefined)

    if (mode() === "signup") {
      const su = await a.licenseSignup?.({ email: email().trim(), password: password() })
      if (!su?.ok) {
        setBusy(false)
        setError(loginErrorMessage(su?.reason))
        return
      }
    }

    const result = await a.licenseLogin({ email: email().trim(), password: password() })
    if (result.authenticated) {
      // Relaunch so the backend (sidecar) boots WITH the license token in env —
      // it spawned before login on first run, so it doesn't have the gateway
      // routing yet. On relaunch, the cached license sets the env before the
      // sidecar starts, and the gate sees an authenticated license → straight in.
      setNotice("Signed in — starting your workspace…")
      if (a.relaunch) {
        a.relaunch()
        return
      }
      setBusy(false)
      mutate({ gated: true, status: result })
    } else {
      setBusy(false)
      if (result.needsUpdate) setError("This version is out of date. Please update NgageCode to continue.")
      else setError(loginErrorMessage(result.reason))
    }
  }

  return (
    <Switch fallback={props.children}>
      {/* Loading the gate decision — render nothing briefly to avoid a flash. */}
      <Match when={gate.loading}>
        <div class="h-full w-full bg-background-base" />
      </Match>

      {/* Gated + not authenticated → login screen. */}
      <Match when={gate()?.gated && !gate()?.status?.authenticated}>
        <div class="h-full w-full flex flex-col items-center justify-center bg-background-base px-4">
          <Logo class="w-40 opacity-90 mb-8" />
          <div class="w-full max-w-sm flex flex-col gap-4">
            <div class="text-center">
              <div class="text-16-medium text-text-strong">
                {mode() === "signup" ? "Create your NgageCode account" : "Sign in to NgageCode"}
              </div>
              <div class="text-13-regular text-text-weak mt-1">
                Industrial AI for IFS Cloud. Your workspace is licensed per organization.
              </div>
            </div>
            <Show when={gate()?.status?.needsUpdate}>
              <div class="text-13-regular text-icon-critical-base text-center">
                Update required — please install the latest NgageCode to continue.
              </div>
            </Show>
            <form onSubmit={doSubmit} class="flex flex-col gap-3">
              <input
                type="email"
                required
                placeholder="you@company.com"
                autocomplete="username"
                value={email()}
                onInput={(e) => setEmail(e.currentTarget.value)}
                class="px-3 py-2 rounded bg-background-stronger border border-border-base text-13-regular text-text-strong outline-none focus:border-icon-accent-base"
              />
              <input
                type="password"
                required
                placeholder={mode() === "signup" ? "Choose a password (8+ chars)" : "Password"}
                autocomplete={mode() === "signup" ? "new-password" : "current-password"}
                value={password()}
                onInput={(e) => setPassword(e.currentTarget.value)}
                class="px-3 py-2 rounded bg-background-stronger border border-border-base text-13-regular text-text-strong outline-none focus:border-icon-accent-base"
              />
              <Show when={error()}>
                <div class="text-12-regular text-icon-critical-base">{error()}</div>
              </Show>
              <Show when={notice()}>
                <div class="text-12-regular text-text-weak">{notice()}</div>
              </Show>
              <Button type="submit" size="large" class="justify-center" disabled={busy()}>
                {busy() ? "Please wait…" : mode() === "signup" ? "Create account" : "Sign in"}
              </Button>
            </form>
            <button
              type="button"
              class="text-12-regular text-text-weak hover:text-text-strong text-center"
              onClick={() => {
                setError(undefined)
                setMode(mode() === "login" ? "signup" : "login")
              }}
            >
              {mode() === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
            </button>
            <div class="text-11-regular text-text-weakest text-center">
              By continuing you agree to your organization's NgageCode usage terms.
            </div>
          </div>
        </div>
      </Match>

      {/* Authenticated (or not gated) → the app. */}
      <Match when={true}>{props.children}</Match>
    </Switch>
  )
}

function loginErrorMessage(reason?: string): string {
  switch (reason) {
    case "invalid_credentials":
      return "Invalid email or password."
    case "email_taken":
      return "An account with this email already exists. Sign in instead."
    case "weak_credentials":
      return "Password must be at least 8 characters."
    case "user_disabled":
      return "This account has been disabled. Contact your administrator."
    case "device_limit":
      return "Device limit reached for your plan. Sign out on another device or upgrade."
    case "device_revoked":
      return "This device has been revoked. Contact your administrator."
    case "org_suspended":
      return "Your subscription is not active. Contact your administrator."
    case "no_gateway_configured":
      return "No NgageCode Cloud gateway is configured for this build."
    default:
      return reason ? `Failed (${reason}).` : "Something went wrong. Please try again."
  }
}
